// src/shared/components/form-date-picker.tsx
// FormDatePicker — the react-hook-form adapter for the DS DatePicker atom (PROD-1500).
//
// Most PA forms keep date fields as `YYYY-MM-DD` STRINGS (the native-input legacy + the wire
// formats want strings); the DS DatePicker speaks `Date`. This adapter keeps the form contract
// unchanged — the field stays a string — and converts at the edge via the local-date helpers
// (LOCAL calendar both ways; see src/shared/lib/local-date.ts for the UTC off-by-one classes
// this avoids). Use it to replace every raw `<input type="date">` (slices A–D of the sweep);
// do NOT hand-roll per-site conversions.
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { DatePicker } from '@scripsteam/scrips-signal-ds';
import { dateToYmd, ymdToLocalDate } from '@/shared/lib/local-date';

export interface FormDatePickerProps<T extends FieldValues> {
  control: Control<T>;
  /** Form field name — the field's value is a `YYYY-MM-DD` string ('' = unset). */
  name: FieldPath<T>;
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
  disabled?: boolean;
  /** Border-only error state; pair with the surrounding form's own error text. */
  invalid?: boolean;
  /** DS DatePicker's own label/error — leave unset when the host renders its own field chrome. */
  label?: string;
  error?: string;
  id?: string;
}

export function FormDatePicker<T extends FieldValues>({
  control, name, minDate, maxDate, placeholder, disabled, invalid, label, error, id,
}: FormDatePickerProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <DatePicker
          id={id}
          label={label}
          error={error}
          invalid={invalid}
          // RHF focus target for shouldFocusError on invalid submit: the DatePicker forwards its
          // ref to a non-focusable div, so register the inner trigger BUTTON with RHF instead
          // (Codex review). Falls back to the div if the internals ever change.
          ref={(el: HTMLDivElement | null) => field.ref(el?.querySelector('button') ?? el)}
          value={ymdToLocalDate(field.value as string)}
          onChange={(d) => {
            field.onChange(dateToYmd(d));
            // The DS DatePicker exposes no onBlur; marking touched on selection keeps
            // touched-based validation modes (onBlur/onTouched) working for this field.
            field.onBlur();
          }}
          minDate={minDate}
          maxDate={maxDate}
          placeholder={placeholder}
          disabled={disabled}
        />
      )}
    />
  );
}
